var _excluded = ["error", "labelProps", "helperText", "maxChars", "isDisabled", "isReadOnly", "isViewMode", "isLoading", "size", "isRequired", "showOptionalIndicator", "children", "render", "data-testid", "id", "colSpan", "name", "control"],
    _excluded2 = ["isHidden"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { forwardRef, useState } from 'react';
import { useTestId } from '../../../../utils';
import { useMultiStyleConfig } from '../../../../utils/use-style-config';
import { Text, Typography } from '../../../dataDisplay';
import { Box, useFormContext } from '../../../internal';
import { FormFieldControlRender, MaxCharsCounter } from './components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var FormField = /*#__PURE__*/forwardRef((_ref, ref) => {
  var _ref2;

  var {
    error,
    labelProps,
    helperText,
    maxChars,
    isDisabled,
    isReadOnly,
    isViewMode,
    isLoading,
    size,
    isRequired,
    showOptionalIndicator,
    children,
    render,
    'data-testid': dataTestId = 'form-field',
    id,
    colSpan = 1,
    name,
    control
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  var isInvalid = !!error;

  var {
    isHidden: isHiddenLabel
  } = labelProps,
      otherLabelProps = _objectWithoutProperties(labelProps, _excluded2);

  var [localValue, setLocalValue] = useState('');
  var getTestId = useTestId(dataTestId);
  var formContext = useFormContext();
  var isFieldDisabled = isDisabled !== null && isDisabled !== void 0 ? isDisabled : formContext.isDisabled;
  var isFieldReadOnly = isReadOnly !== null && isReadOnly !== void 0 ? isReadOnly : formContext.isReadOnly || formContext.isLoading;
  var isFieldInViewMode = isViewMode !== null && isViewMode !== void 0 ? isViewMode : formContext.isViewMode;
  var fieldSize = (_ref2 = size !== null && size !== void 0 ? size : formContext.size) !== null && _ref2 !== void 0 ? _ref2 : 'large';
  var styles = useMultiStyleConfig('FormField', {
    size: fieldSize,
    error,
    helperText,
    isViewMode: isFieldInViewMode
  });
  var fieldId = id !== null && id !== void 0 ? id : labelProps.id; // Create IDs for the helper text and max chars elements

  var helperTextId = helperText ? "".concat(id, "-helper-text") : undefined;
  var maxCharsId = maxChars ? "".concat(id, "-max-chars") : undefined;
  /**
   * `aria-describedby` is necessary to associate the input with the helper text and max chars elements.
   * attribute expects an ([ID reference list](https://www.w3.org/TR/wai-aria/#typemapping)) as it's value. Multiple IDs can be provided, separated by spaces.
   */

  var fieldAriaDescribedby = "".concat(helperTextId !== null && helperTextId !== void 0 ? helperTextId : '', " ").concat(maxCharsId !== null && maxCharsId !== void 0 ? maxCharsId : '').trim();

  var renderFieldProps = _objectSpread(_objectSpread({
    size: fieldSize,
    isDisabled: isFieldDisabled,
    isReadOnly: isFieldReadOnly,
    isViewMode: isFieldInViewMode,
    isLoading,
    isInvalid
  }, getTestId('render-field')), {}, {
    id: fieldId,
    'aria-describedby': fieldAriaDescribedby,
    'aria-required': !isFieldInViewMode && isRequired
  }, maxChars && {
    maxChars
  });

  return /*#__PURE__*/_jsxs(Box, _objectSpread(_objectSpread(_objectSpread({
    __css: styles['container']
  }, getTestId()), {}, {
    "data-component": "FormField",
    gridColumn: {
      xs: '1 / 1',
      s: "span ".concat(colSpan, " / span 1")
    }
  }, props), {}, {
    ref: ref,
    children: [/*#__PURE__*/_jsx(Box, _objectSpread(_objectSpread({
      __css: styles['label']
    }, getTestId('label-container')), {}, {
      "data-hidden": labelProps.isHidden,
      children: /*#__PURE__*/_jsx(Typography.Label, _objectSpread(_objectSpread(_objectSpread({}, getTestId('label')), {}, {
        isInvalid: isInvalid,
        isReadOnly: isFieldReadOnly || isLoading,
        isViewMode: isFieldInViewMode,
        isDisabled: isFieldDisabled,
        description: isFieldInViewMode ? undefined : isRequired ? '*' : showOptionalIndicator ? '(optional)' : undefined
      }, otherLabelProps), {}, {
        id: fieldId
      }))
    })), /*#__PURE__*/_jsxs(Box, {
      __css: styles['fieldGroup'],
      children: [/*#__PURE__*/_jsx(Box, {
        __css: styles['field'],
        children: control && name ? /*#__PURE__*/_jsx(FormFieldControlRender, _objectSpread({
          control: control,
          name: name,
          render: render,
          setLocalValue: setLocalValue
        }, renderFieldProps)) : render(renderFieldProps)
      }), /*#__PURE__*/_jsxs(Box, {
        __css: styles['helperText'],
        "aria-disabled": isFieldDisabled,
        "aria-invalid": isInvalid,
        "data-readonly": isFieldReadOnly || isLoading || null,
        "aria-live": "assertive",
        children: [error !== null && error !== void 0 && error.message ? /*#__PURE__*/_jsx(Text, _objectSpread(_objectSpread({
          color: "inherit",
          textStyle: "inline",
          id: helperTextId,
          shouldSupportEllipsis: true
        }, getTestId('error-message')), {}, {
          children: error.message
        })) : helperText && !isFieldInViewMode ? /*#__PURE__*/_jsx(Text, _objectSpread(_objectSpread({
          color: "inherit",
          textStyle: "inline",
          id: helperTextId,
          shouldSupportEllipsis: true
        }, getTestId('helper-text')), {}, {
          children: helperText
        })) : null, !isFieldInViewMode && typeof localValue === 'string' && maxChars && /*#__PURE__*/_jsx(MaxCharsCounter, _objectSpread({
          maxChars: maxChars,
          id: maxCharsId,
          valueLength: localValue.length
        }, getTestId('chars-counter')))]
      })]
    })]
  }));
});
FormField.displayName = 'FormField';
//# sourceMappingURL=FormField.js.map